import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'
import { $api } from '../api/queryClient'

// Hooks for physical QR labels (spec 2026-08-17-03).
//
// Two audiences, two auth models, in one file because they are one workflow:
//   - the PUBLIC scan page under /q/{code}, where the label code in the URL is
//     the whole request and NO Authorization header must ever be sent — the
//     reader is a resident with no account, standing in a corridor;
//   - the syndic side under /api/v1, authenticated like everything else.
//
// Mirrors useWorkOrder.ts, which solves the same shape for the provider portal.

// ---- Types ----

export type LabelStatus = 'unassigned' | 'assigned' | 'retired'
export type LabelMaterial = 'paper' | 'polypropylene' | 'polyester' | 'polyester_ht'

export interface PublicLabelView {
    code: string
    asset_type_label?: string
    asset_name?: string
    location?: string
    org_display_name?: string
    // Member-only, populated by the authenticated resolve endpoint.
    label_uid?: string
    org_code?: string
    asset_uid?: string
    status?: LabelStatus
}

export interface AssetLabel {
    uid: string
    code: string
    status: LabelStatus
    material?: LabelMaterial
    location_hint?: string
    printed_at?: string
    assigned_at?: string
    retired_at?: string
    asset_uid?: string
    asset_name?: string
    created_at: string
    updated_at: string
}

export interface LabelOrderItem {
    uid: string
    label_code?: string
    human_label: string
    material: LabelMaterial
    sort_key?: string
}

export interface LabelOrder {
    uid: string
    status: 'draft' | 'submitted' | 'in_production' | 'shipped' | 'applied' | 'cancelled'
    asset_uid?: string
    shipping_address?: Record<string, unknown>
    contact_email?: string
    notes?: string
    tracking?: string
    submitted_at?: string
    shipped_at?: string
    item_count?: number
    items?: LabelOrderItem[]
    created_at: string
}

// ---- Public scan page (no auth) ----

const scanBase = (code: string) => `${apiBaseUrl}/q/${encodeURIComponent(code)}`

/**
 * ClosedLabelView is the 410 body a retired label answers with (spec
 * 2026-09-10-01) — the one public failure that is NOT the indistinguishable
 * 404, because the sticker is glued to a wall and cannot be recalled.
 */
export interface ClosedLabelView {
    code: 'endpoint_closed'
    title?: string
    label_code?: string
    org_display_name?: string
    contact_address?: string
}

export interface ScanError {
    status: number
    code?: string
    title?: string
    label_code?: string
    org_display_name?: string
    contact_address?: string
}

/**
 * scanFetch deliberately sends no Authorization header. Every unresolvable
 * request — unknown code, retired label, deleted organization — comes back as
 * a bare 404 with a non-JSON body, so the error path never tries to parse one.
 *
 * The exception is 410: a closed label answers with a payload the page renders
 * (organization name, contact address), which is why the body is spread onto
 * the thrown value rather than dropped.
 */
async function scanFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, options)
    if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw { status: res.status, ...body }
    }
    return res.json()
}

/**
 * closedLabelOf reads the retired-label payload out of a thrown scan error,
 * or null when the failure was anything else.
 */
export function closedLabelOf(error: unknown): ClosedLabelView | null {
    const e = error as ScanError | null
    if (!e || e.status !== 410) return null
    return {
        code: 'endpoint_closed',
        title: e.title,
        label_code: e.label_code,
        org_display_name: e.org_display_name,
        contact_address: e.contact_address,
    }
}

export const scanKey = (code: string) => ['label-scan', code] as const

export function useLabelScan(code: string) {
    return useQuery({
        queryKey: scanKey(code),
        queryFn: () => scanFetch<PublicLabelView>(`${scanBase(code)}/state`),
        enabled: !!code,
        retry: false,
    })
}

/**
 * What a resident standing in front of the sticker actually sends (spec
 * 2026-09-10-02): a spoken note, photographs, and — optionally — a way to reach
 * them back. `description` survives because the endpoint still accepts text;
 * the page itself no longer asks for any.
 */
export interface LabelReportInput {
    description?: string
    contact_email?: string
    contact_phone?: string
    voice?: Blob | null
    photos?: File[]
}

export function useSubmitLabelReport(code: string) {
    return useMutation({
        mutationFn: (input: LabelReportInput) => {
            const photos = input.photos ?? []
            const url = `${scanBase(code)}/report`

            // JSON while there is nothing to carry, multipart the moment there
            // is — one route, two shapes, exactly as the resident page posts.
            if (!input.voice && photos.length === 0) {
                return scanFetch<{ acknowledged: boolean; conversation_url?: string }>(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        description: input.description ?? '',
                        contact_email: input.contact_email,
                        contact_phone: input.contact_phone,
                    }),
                })
            }

            const form = new FormData()
            if (input.description) form.append('description', input.description)
            if (input.contact_email) form.append('contact_email', input.contact_email)
            if (input.contact_phone) form.append('contact_phone', input.contact_phone)
            if (input.voice) {
                // Safari records into mp4/aac, everyone else into webm/opus.
                const extension = input.voice.type.includes('mp4') ? 'm4a' : 'webm'
                form.append('voice', input.voice, `voice-note.${extension}`)
            }
            for (const photo of photos) {
                form.append('files', photo, photo.name || 'photo.jpg')
            }

            // No Content-Type header on purpose: only the browser knows the
            // multipart boundary it just generated.
            return scanFetch<{ acknowledged: boolean; conversation_url?: string }>(url, {
                method: 'POST',
                body: form,
            })
        },
    })
}

/**
 * storedAccessToken reads the session token the way AuthContext persists it:
 * inside the `authState` blob, not under a bare `token` key.
 *
 * The public scan page cannot use the useAuth() hook — it renders outside the
 * app shell, for a reader who usually has no session at all — so it reads the
 * same storage directly, and tolerates its absence.
 */
export function storedAccessToken(): string | null {
    if (typeof window === 'undefined') return null
    try {
        const raw = localStorage.getItem('authState')
        if (!raw) return null
        return (JSON.parse(raw) as { accessToken?: string | null }).accessToken ?? null
    } catch {
        return null
    }
}

/**
 * useMemberLabelResolve is the authenticated twin of useLabelScan: it adds the
 * org code and asset uid needed to deep-link into the app, and it sees
 * unassigned labels, which the public route hides.
 *
 * It only fires when a token is present — an anonymous visitor must never
 * trigger it, both because it would 401 and because the whole point of the
 * split is that the public page asks for nothing it is not entitled to.
 */
export function useMemberLabelResolve(code: string) {
    const token = storedAccessToken()

    return useQuery({
        queryKey: ['label-resolve-member', code] as const,
        queryFn: async () => {
            const res = await fetch(`${apiBaseUrl}/api/v1/labels/${encodeURIComponent(code)}/resolve`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            if (!res.ok) {
                throw { status: res.status }
            }
            return (await res.json()) as PublicLabelView
        },
        enabled: !!token && !!code,
        retry: false,
    })
}

// ---- Syndic side (authenticated, org-scoped) ----

export function useAssetLabels(orgCode: string, assetUid: string) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/labels',
        { params: { path: { orgCode, assetUid } } },
        { enabled: !!orgCode && !!assetUid },
    )
}

/**
 * invalidateAssetLabels follows the repo's cache-invalidation convention:
 * match on method + path, then check the path params, because the generated
 * query keys are ['get', path, { params: … }] and a shallow destructure of the
 * key does not match them.
 */
function invalidateAssetLabels(queryClient: ReturnType<typeof useQueryClient>, orgCode: string, assetUid?: string) {
    queryClient.invalidateQueries({
        predicate: query => {
            const key = query.queryKey as unknown[]
            if (key.length < 2 || key[0] !== 'get') return false
            const path = key[1]
            if (
                path !== '/api/v1/organizations/{orgCode}/assets/{assetUid}/labels' &&
                path !== '/api/v1/organizations/{orgCode}/labels'
            ) {
                return false
            }
            const params = (
                key.find(item => item && typeof item === 'object' && 'params' in item) as
                    | { params?: { path?: { orgCode?: string; assetUid?: string } } }
                    | undefined
            )?.params
            if (params?.path?.orgCode !== orgCode) return false
            if (assetUid && params?.path?.assetUid && params.path.assetUid !== assetUid) return false
            return true
        },
    })
}

export function useCreateAssetLabels(orgCode: string, assetUid: string) {
    const queryClient = useQueryClient()
    return $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/labels', {
        onSuccess: () => invalidateAssetLabels(queryClient, orgCode, assetUid),
    })
}

export function useUpdateLabel(orgCode: string, assetUid?: string) {
    const queryClient = useQueryClient()
    return $api.useMutation('patch', '/api/v1/organizations/{orgCode}/labels/{labelUid}', {
        onSuccess: () => invalidateAssetLabels(queryClient, orgCode, assetUid),
    })
}

export function useRetireLabel(orgCode: string, assetUid?: string) {
    const queryClient = useQueryClient()
    return $api.useMutation('delete', '/api/v1/organizations/{orgCode}/labels/{labelUid}', {
        onSuccess: () => invalidateAssetLabels(queryClient, orgCode, assetUid),
    })
}

export function useLabelOrders(orgCode: string) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/label-orders',
        { params: { path: { orgCode } } },
        { enabled: !!orgCode },
    )
}

export function useCreateLabelOrder() {
    const queryClient = useQueryClient()
    return $api.useMutation('post', '/api/v1/organizations/{orgCode}/label-orders', {
        onSuccess: () => {
            queryClient.invalidateQueries({
                predicate: query => {
                    const key = query.queryKey as unknown[]
                    return key.length >= 2 && key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/label-orders'
                },
            })
        },
    })
}

export interface LabelSheetOptions {
    assetUid?: string
    format?: string
    offsetX?: number
    offsetY?: number
    startIndex?: number
    calibration?: boolean
    status?: string
    lang?: string
    // How many times each label is repeated on the sheet. One sticker per
    // equipment is the registry rule; printing that one code several times is a
    // separate decision — spares get lost, and a machine you can walk around
    // wants the same code on two faces.
    copies?: number
}

/**
 * labelSheetUrl builds the print URL. The offset parameters are not a nicety:
 * every printer drifts a millimetre or two, and without them the sheet is
 * unusable on half the machines it meets.
 */
export function labelSheetUrl(orgCode: string, opts: LabelSheetOptions): string {
    const base = opts.assetUid
        ? `${apiBaseUrl}/api/v1/organizations/${orgCode}/assets/${opts.assetUid}/labels.pdf`
        : `${apiBaseUrl}/api/v1/organizations/${orgCode}/labels/sheet.pdf`

    const params = new URLSearchParams()
    if (opts.format) params.set('format', opts.format)
    if (opts.offsetX) params.set('offset_x', String(opts.offsetX))
    if (opts.offsetY) params.set('offset_y', String(opts.offsetY))
    if (opts.startIndex) params.set('start_index', String(opts.startIndex))
    if (opts.calibration) params.set('calibration', '1')
    if (opts.status) params.set('status', opts.status)
    if (opts.lang) params.set('lang', opts.lang)
    if (opts.copies && opts.copies > 1) params.set('copies', String(opts.copies))

    const query = params.toString()
    return query ? `${base}?${query}` : base
}

export type AssetKind = "equipment" | "location" | "structural" | "trade" | "other"

export interface LabelRollOptions {
    assetUid: string
    // Which asset kinds to include. Every asset carries a label from birth, so
    // without this the roll would contain lots, floors and trade groupings.
    kinds?: AssetKind[]
    width?: number
    height?: number
    bleed?: number
    cutMarks?: boolean
    lang?: string
}

/**
 * openLabelRoll fetches the production file for a label converter: every label
 * under the asset, one page per label at die size.
 *
 * Separate from openLabelSheet because the two artefacts are for different
 * people. The sheet is A4 die-cut stock for the office laser; this is what you
 * e-mail a converter to have a roll made on laminated polyester.
 */
export async function openLabelRoll(orgCode: string, opts: LabelRollOptions): Promise<void> {
    const params = new URLSearchParams()
    if (opts.width) params.set("width", String(opts.width))
    if (opts.height) params.set("height", String(opts.height))
    if (opts.bleed) params.set("bleed", String(opts.bleed))
    if (opts.cutMarks) params.set("cut_marks", "1")
    if (opts.lang) params.set("lang", opts.lang)
    for (const kind of opts.kinds ?? []) params.append("kind", kind)

    const query = params.toString()
    const url =
        `${apiBaseUrl}/api/v1/organizations/${orgCode}/assets/${opts.assetUid}/labels-roll.pdf` +
        (query ? `?${query}` : "")

    const res = await fetch(url, { headers: { Authorization: `Bearer ${storedAccessToken()}` } })
    if (!res.ok) {
        throw new Error(await res.text())
    }

    const blob = new Blob([await res.blob()], { type: "application/pdf" })
    const objectURL = window.URL.createObjectURL(blob)
    window.open(objectURL, "_blank", "noopener")
    setTimeout(() => window.URL.revokeObjectURL(objectURL), 60_000)
}

/**
 * openLabelSheet fetches the PDF and hands it to the browser as a blob.
 *
 * A plain <a href> cannot be used: the endpoint is authenticated by the
 * Authorization header, which a navigation request does not carry, so the link
 * would silently 401. Same approach as AssetsExportPage.
 */
export async function openLabelSheet(orgCode: string, opts: LabelSheetOptions): Promise<void> {
    const res = await fetch(labelSheetUrl(orgCode, opts), {
        headers: { Authorization: `Bearer ${storedAccessToken()}` },
    })

    if (!res.ok) {
        throw new Error(await res.text())
    }

    const blob = new Blob([await res.blob()], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener')
    // Revoke late: revoking immediately races the new tab's load.
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000)
}

/**
 * useLabelQR fetches the label's QR as an inline SVG string.
 *
 * It goes through fetch rather than an <img src> because the endpoint is
 * authenticated by the Authorization header, which a plain image request does
 * not carry. Inlining the markup also lets the SVG inherit its box from CSS
 * instead of shipping a fixed raster size.
 *
 * The QR for a given label never changes — the code is immutable — so this is
 * cached indefinitely on the client, matching the endpoint's own Cache-Control.
 */
export function useLabelQR(orgCode: string, labelUid: string, enabled = true) {
    return useQuery({
        queryKey: ['label-qr', orgCode, labelUid] as const,
        queryFn: async () => {
            const res = await fetch(
                `${apiBaseUrl}/api/v1/organizations/${orgCode}/labels/${labelUid}/qr.svg`,
                { headers: { Authorization: `Bearer ${storedAccessToken()}` } },
            )
            if (!res.ok) {
                throw new Error(`QR preview failed: ${res.status}`)
            }
            return res.text()
        },
        enabled: enabled && !!orgCode && !!labelUid,
        staleTime: Infinity,
        gcTime: Infinity,
    })
}
